import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileText, Loader2, Trash2, Upload, FileSpreadsheet } from "lucide-react";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { CATEGORIE_DOC, ROLE_LABEL, fmtDate } from "@/lib/constants";
import { AIUTI } from "@/lib/guide";
import { Hint } from "@/components/Guida";
import { PmBot } from "@/components/PmBot";

const AREE = ["fornitore", "pm", "dev", "tester", "cliente_finale"];

export default function Documenti() {
  const { user } = useAuth();
  const { projectId } = useProject();
  const [docs, setDocs] = useState(null);
  const [file, setFile] = useState(null);
  const [titolo, setTitolo] = useState("");
  const [categoria, setCategoria] = useState(CATEGORIE_DOC[0]);
  const [visibilita, setVisibilita] = useState(["fornitore", "pm", "dev", "tester", "cliente_finale"]);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState("");

  const load = () =>
    api
      .get(`/projects/${projectId}/documents`)
      .then(({ data }) => setDocs(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));

  useEffect(() => {
    if (!projectId) return;
    setDocs(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Seleziona un file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("titolo", titolo);
    fd.append("categoria", categoria);
    fd.append("visibilita", visibilita.join(","));
    try {
      await api.post(`/projects/${projectId}/documents`, fd);
      toast.success("Documento caricato");
      setFile(null);
      setTitolo("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const scarica = async (d) => {
    setDownloading(d.id);
    try {
      await downloadFile(`/documents/${d.id}/download`, d.original_filename);
    } catch (err) {
      toast.error("Errore nel download del documento");
    }
    setDownloading("");
  };

  const elimina = async (d) => {
    try {
      await api.delete(`/documents/${d.id}`);
      setDocs((list) => list.filter((x) => x.id !== d.id));
      toast.success("Documento rimosso");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportPiano = async (vista) => {
    setDownloading(`piano-${vista}`);
    try {
      await downloadFile(`/projects/${projectId}/export/excel?vista=${vista}`, `Piano_${vista}.xlsx`);
      toast.success("Excel generato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setDownloading("");
  };

  if (!docs)
    return (
      <div className="h-64 grid place-items-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  const isAdmin = user.role === "admin";
  const visteDisponibili = isAdmin
    ? [["interno", "Piano Interno completo"], ["cliente", "Piano Majorbit"], ["fornitore", "Piano Stefano Critelli"], ["cliente_finale", "Piano Cliente Finale"]]
    : ["pm", "dev", "tester"].includes(user.role)
    ? [["cliente", "Piano Majorbit"], ["cliente_finale", "Piano Cliente Finale"]]
    : user.role === "fornitore"
    ? [["fornitore", "Piano Stefano Critelli"]]
    : [["cliente_finale", "Piano Cliente Finale"]];

  return (
    <div className="space-y-6" data-testid="documenti-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Archivio di progetto</p>
        <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Documenti</h1>
        <p className="mt-2 text-sm text-slate-600">
          Analisi funzionale e tecnica, piani e verbali. Vedi solo i documenti abilitati per la tua area
          ({ROLE_LABEL[user.role]}).
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="generated-exports-card">
        <h3 className="text-lg font-semibold text-slate-900">Excel generati in tempo reale</h3>
        <p className="text-sm text-slate-500">Sempre aggiornati con le percentuali e gli interventi più recenti.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {visteDisponibili.map(([vista, label]) => (
            <Button key={vista} onClick={() => exportPiano(vista)} disabled={downloading === `piano-${vista}`}
                    variant="outline"
                    className="h-10 bg-white border-slate-300 hover:bg-slate-900 hover:text-white transition-colors"
                    data-testid={`export-piano-${vista}-button`}>
              {downloading === `piano-${vista}` ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <FileSpreadsheet className="h-4 w-4 mr-2" />
              )}
              {label}
            </Button>
          ))}
        </div>
      </div>

      {isAdmin && (
        <form onSubmit={upload} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4"
              data-testid="upload-document-form">
          <h3 className="text-lg font-semibold text-slate-900">Carica un documento</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">File</Label>
              <Input type="file" accept=".pdf,.xlsx,.xls,.docx,.csv,.txt,.png,.jpg"
                     onChange={(e) => setFile(e.target.files[0])} className="mt-1.5 h-10"
                     data-testid="document-file-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Titolo</Label>
              <Input value={titolo} onChange={(e) => setTitolo(e.target.value)}
                     placeholder="es. Specifiche funzionali v.3" className="mt-1.5 h-10"
                     data-testid="document-title-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Categoria</Label>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger className="mt-1.5 h-10" data-testid="document-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {CATEGORIE_DOC.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Visibile alle aree
              <Hint id="visibilita" testo={AIUTI.visibilita} />
            </Label>
            <div className="mt-2 flex flex-wrap gap-4">
              {AREE.map((a) => (
                <label key={a} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <Checkbox checked={visibilita.includes(a)}
                            onCheckedChange={(v) =>
                              setVisibilita((list) => (v ? [...list, a] : list.filter((x) => x !== a)))
                            }
                            data-testid={`document-visibility-${a}`} />
                  {ROLE_LABEL[a]}
                </label>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={busy}
                  className="h-10 bg-slate-900 text-white hover:bg-slate-800 hover:text-white transition-colors"
                  data-testid="upload-document-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            Carica documento
          </Button>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {docs.map((d) => (
          <div key={d.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex items-start gap-4"
               data-testid={`document-card-${d.id}`}>
            <div className="h-11 w-11 rounded-lg bg-slate-100 grid place-items-center shrink-0">
              <FileText className="h-5 w-5 text-slate-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-900 leading-snug truncate">{d.titolo}</p>
              <p className="mt-0.5 font-mono text-[11px] text-slate-500 truncate">{d.original_filename}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <span className="font-mono text-[10px] px-2 py-0.5 rounded-full border border-slate-200 text-slate-600">
                  {d.categoria}
                </span>
                {(d.visibilita || []).map((v) => (
                  <span key={v} className="font-mono text-[10px] px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700">
                    {ROLE_LABEL[v]}
                  </span>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-slate-400 font-mono">
                {Math.round((d.size || 0) / 1024)} KB · caricato il {fmtDate(d.created_at)} da {d.created_by}
              </p>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => scarica(d)} disabled={downloading === d.id}
                      className="h-8 text-slate-600 hover:bg-slate-900 hover:text-white transition-colors"
                      data-testid={`document-download-${d.id}`}>
                {downloading === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              </Button>
              {isAdmin && (
                <Button variant="ghost" size="sm" onClick={() => elimina(d)}
                        className="h-8 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                        data-testid={`document-delete-${d.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
        {docs.length === 0 && (
          <p className="lg:col-span-2 text-center text-sm text-slate-400 py-12 bg-white border border-slate-200 rounded-xl"
             data-testid="no-documents-message">
            Nessun documento disponibile per la tua area.
          </p>
        )}
      </div>

      <PmBot pagina="dashboard" />

    </div>
  );
}
